import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_EPOCUS_BILLING_COLLECTION_NAME = "epocus-billing";

export const useRxCollectionEpocusBilling =
  (): RxCollection<EpocusBilling> | null =>
    useRxCollection<EpocusBilling>(RXDB_EPOCUS_BILLING_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<EpocusBilling>>;
export type EpocusBillingQueryConstructor = UseRxData["1"];

export const DOCUMENTATION_STATUSES = [
  "COMPLETE",
  "INCOMPLETE",
  "REVIEWED",
] as const;
export type DocumentationStatus = (typeof DOCUMENTATION_STATUSES)[number];

export const CLINICAL_SETTINGS = [
  "ED",
  "INPATIENT",
  "OUTPATIENT",
  "OFFICE",
  "HOSPITAL_OUTPATIENT",
  "ASC",
] as const;
export type ClinicalSetting = (typeof CLINICAL_SETTINGS)[number];

export const BILLING_MODES = ["COMPONENT", "GLOBAL"] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

export type ProcedureCode = {
  cpt: string;
  modifier: string | null;
  sideModifier?: string | null;
  units: number;
  systemSuggested: boolean;
  performerConfirmed: boolean;
  overrideReason: string | null;
};

export type DiagnosisCode = {
  code: string;
  codingSystem: "ICD-10-CM" | "ICD-11";
  description: string;
};

const EPOCUS_BILLING_SCHEMA_LITERAL = {
  title: "epocus billing schema",
  version: 2,
  description:
    "A billing record for a single POCUS exam, capturing CPT/ICD codes and documentation sufficiency status",
  type: "object",
  primaryKey: "id",
  properties: {
    id: { type: "string", maxLength: 100 },
    examId: { type: ["string", "null"], maxLength: 100 },
    encounterId: { type: "string", maxLength: 100 },
    programId: { type: ["string", "null"], maxLength: 100 },
    renderingProviderUserId: { type: ["string", "null"], maxLength: 100 },
    dateOfService: { type: ["string", "null"], format: "date-time" },
    patientMrn: { type: ["string", "null"] },
    patientDob: { type: ["string", "null"], format: "date-time" },
    payerName: { type: ["string", "null"] },
    policyNumber: { type: ["string", "null"] },
    performingPhysicianName: { type: ["string", "null"] },
    performingPhysicianNpi: { type: ["string", "null"] },
    supervisingPhysicianNpi: { type: ["string", "null"] },
    facilityNpi: { type: ["string", "null"] },
    clinicalSetting: {
      type: ["string", "null"],
      enum: [...CLINICAL_SETTINGS, null],
    },
    billingMode: { type: ["string", "null"] },
    // biome-ignore lint/suspicious/noExplicitAny: ProcedureCode[] — type enforced via TypeScript override below
    procedureCodes: { type: "array", items: { type: "object" } } as any,
    // biome-ignore lint/suspicious/noExplicitAny: DiagnosisCode[] — type enforced via TypeScript override below
    diagnosisCodes: { type: "array", items: { type: "object" } } as any,
    documentationStatus: { type: ["string", "null"] },
    imagesStored: { type: "boolean" },
    exportedAt: { type: ["string", "null"], format: "date-time" },
    locked: { type: "boolean" },
    meta: SYNCABLE_META,
  },
  required: [
    "id",
    "encounterId",
    "programId",
    "renderingProviderUserId",
    "procedureCodes",
    "diagnosisCodes",
    "imagesStored",
    "locked",
    "meta",
  ],
  indexes: ["encounterId"],
  encrypted: [
    "patientMrn",
    "patientDob",
    "payerName",
    "policyNumber",
    "performingPhysicianName",
    "performingPhysicianNpi",
    "supervisingPhysicianNpi",
    "facilityNpi",
  ],
  /*
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_BILLING_SCHEMA_LITERAL);

type RawEpocusBilling = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export type EpocusBilling = Omit<
  RawEpocusBilling,
  "procedureCodes" | "diagnosisCodes"
> & {
  procedureCodes: ProcedureCode[];
  diagnosisCodes: DiagnosisCode[];
};

export type EpocusBillingCollection = RxCollection<EpocusBilling>;

export const EPOCUS_BILLING_SCHEMA: RxJsonSchema<EpocusBilling> =
  EPOCUS_BILLING_SCHEMA_LITERAL as unknown as RxJsonSchema<EpocusBilling>;
